import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  // The mockup's .btn: compact, flat, 150ms transitions, no shadow. The default size takes the Input's
  // 8px vertical padding rather than the mockup's 7px, so a toolbar's boxes and buttons line up (KP, Q43).
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border font-medium cursor-pointer transition-colors duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-3.5 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "border-line bg-panel text-ink hover:bg-panel-2 hover:border-mute/50",
        primary: "border-red bg-red text-primary-foreground hover:bg-red-hi",
        amber: "border-amber/60 bg-amber-bg text-amber font-semibold hover:border-amber",
        ghost: "border-transparent bg-transparent text-ink-2 hover:bg-panel-2 hover:text-ink",
        danger: "border-line bg-panel text-red-hi hover:bg-red-bg",
        destructive: "border-red-hi bg-red-hi text-destructive-foreground hover:bg-red",
        link: "border-transparent bg-transparent text-red underline-offset-4 hover:underline",
      },
      size: {
        default: "px-3 py-2 text-base",
        sm: "px-[9px] py-1 text-sm",
        icon: "h-[30px] w-[30px] p-0",
        iconSm: "h-6 w-6 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
